/**
 * Student-facing lesson fragments when `htmlContent` is missing (offline / API gap).
 * Not teacher training — texts, lexis, and task lines learners actually see.
 */

function esc(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}


/**
 * @param {string} topic
 * @param {{ title?: string } | null} [section]
 */
export function miniReadingParagraph(topic, section = null) {
  const t = String(topic || 'this topic').trim() || 'this topic';
  const st = String(section?.title || '').toLowerCase();

  if (st.includes('vocab') || st.includes('lexis') || st.includes('words')) {
    return `The list below gives you words you need for ${t}. Read the meaning, then read the example. Try to use each word in a new sentence about ${t}.`;
  }
  if (st.includes('lead') || st.includes('warm')) {
    return `You are going to work with the topic ${t}. First, connect ${t} to your own experience. The questions help you think in English before you read or listen more.`;
  }
  if (st.includes('grammar')) {
    return `These sentences are about ${t}. They show one grammar pattern you will practise. Read them aloud and notice how the verb forms change.`;
  }
  if (st.includes('listen')) {
    return `The short script below is about ${t}. Two speakers share opinions. Read it silently first; later your teacher may use audio with the same text.`;
  }
  if (st.includes('speak') || st.includes('discussion')) {
    return `Use the ideas below to speak about ${t}. Each prompt gives you language you can copy and adapt. Your goal is a clear opinion and at least one reason.`;
  }
  if (st.includes('writ')) {
    return `This is a writing task about ${t}. Follow the situation and the bullet points. Use formal or neutral style unless the task says otherwise.`;
  }
  if (st.includes('read')) {
    return `Read the article about ${t}. It gives facts and one clear opinion. After reading, you will check ideas in the exercises — some numbers and names appear only here.`;
  }

  return `People read and talk about ${t} in many countries. Some discussions focus on causes; others focus on solutions. This section gives you language so you can describe ${t} and give your own view in English.`;
}

// Pick `count` items from `pool`, rotating deterministically by topic so each
// lesson gets a different slice but the same lesson always gets the same slice.
function rotatedPick(pool, count, topic) {
  const t = String(topic || '');
  const seed = t.split('').reduce((a, c) => (a + c.charCodeAt(0)) % pool.length, 0);
  const out = [];
  for (let i = 0; i < count; i++) out.push(pool[(seed + i * 3) % pool.length]);
  return out;
}

/**
 * @param {string} topic
 * @param {number} [count]
 */
export function vocabularyItemsForTopic(topic, count = 6) {
  const t = String(topic || 'this topic').trim() || 'this topic';
  const pool = [
    { word: 'impact', definition: `a strong effect that ${t} has on people or the environment` },
    { word: 'evidence', definition: `facts or examples you use when you argue about ${t}` },
    { word: 'perspective', definition: `one way of looking at ${t}; there can be several perspectives` },
    { word: 'challenge', definition: `a difficult part of understanding or dealing with ${t}` },
    { word: 'solution', definition: `an idea or plan that could improve a problem linked to ${t}` },
    { word: 'debate', definition: `a discussion where people disagree politely about ${t}` },
    { word: 'trend', definition: `a general direction in which ${t} is developing` },
    { word: 'consequence', definition: `something that happens as a result of ${t}` },
    { word: 'awareness', definition: `knowing that ${t} exists and understanding why it matters` },
    { word: 'controversy', definition: `public disagreement about ${t}` },
    { word: 'breakthrough', definition: `an important development that changes how we deal with ${t}` },
    { word: 'misconception', definition: `a wrong idea that many people have about ${t}` },
    { word: 'priority', definition: `something connected with ${t} that you have to handle first` },
    { word: 'mindset', definition: `a way of thinking that shapes how people respond to ${t}` },
    { word: 'stakeholder', definition: `a person or group affected by decisions about ${t}` },
  ];
  return rotatedPick(pool, Math.min(count, pool.length), t);
}

/**
 * @param {string} topic
 */
export function gapFillItemsForTopic(topic) {
  const t = String(topic || 'this topic').trim() || 'this topic';
  const pool = [
    { sentence: `Many people have strong **opinions** about ${t} and how it affects everyday life.`, gap: 'opinions' },
    { sentence: `Experts often **disagree** on the best approach to dealing with ${t}.`, gap: 'disagree' },
    { sentence: `A deeper **understanding** of ${t} can help you make better decisions.`, gap: 'understanding' },
    { sentence: `The **impact** of ${t} on society has grown significantly in recent years.`, gap: 'impact' },
    { sentence: `Researchers have **gathered** evidence to study ${t} more carefully.`, gap: 'gathered' },
    { sentence: `Most communities are **affected** by ${t} in one way or another.`, gap: 'affected' },
    { sentence: `Some governments have **invested** heavily in new approaches to ${t}.`, gap: 'invested' },
    { sentence: `Young people are often more **aware** of the issues around ${t}.`, gap: 'aware' },
    { sentence: `One **misconception** about ${t} is that it does not affect ordinary people.`, gap: 'misconception' },
    { sentence: `Activists are calling for a **breakthrough** in how we handle ${t}.`, gap: 'breakthrough' },
    { sentence: `Scientists have **warned** that ignoring ${t} may have serious consequences.`, gap: 'warned' },
    { sentence: `Public **awareness** of ${t} has improved over the last decade.`, gap: 'awareness' },
  ];
  const picks = rotatedPick(pool, 6, t);
  return picks.map((p, i) => ({ id: `mat-gap-${i + 1}`, ...p }));
}

/**
 * @param {string} topic
 */
export function trueFalseStatementsForTopic(topic) {
  const t = String(topic || 'this topic').trim() || 'this topic';
  const pool = [
    { statement: `${t} is discussed by people in many different countries.`, isTrue: true },
    { statement: `Experts agree there is only one correct way to think about ${t}.`, isTrue: false },
    { statement: `Understanding ${t} can help you communicate more clearly in English.`, isTrue: true },
    { statement: `${t} has no practical relevance outside academic settings.`, isTrue: false },
    { statement: `Opinions about ${t} can differ depending on culture and experience.`, isTrue: true },
    { statement: `The vocabulary you learn today is only useful when discussing ${t} in class.`, isTrue: false },
    { statement: `Researchers continue to publish new studies about ${t} every year.`, isTrue: true },
    { statement: `${t} affects only a very small group of people worldwide.`, isTrue: false },
    { statement: `Media coverage of ${t} has increased over the last 10 years.`, isTrue: true },
    { statement: `Solutions to issues connected with ${t} are usually quick and simple.`, isTrue: false },
    { statement: `Personal experience can shape the way someone sees ${t}.`, isTrue: true },
    { statement: `Every country approaches ${t} in exactly the same way.`, isTrue: false },
  ];
  return rotatedPick(pool, 8, t);
}

/**
 * Listening / dialogue script (read-aloud or audio later)
 * @param {string} topic
 */
export function dialogueScriptForTopic(topic) {
  const t = String(topic || 'this topic').trim() || 'this topic';
  const e = esc(t);
  return `<p><strong>A:</strong> So what is the first thing you think of when you hear “${e}”?</p>
<p><strong>B:</strong> For me it is how ${e} shows up in everyday news — not only in textbooks.</p>
<p><strong>A:</strong> I agree. I also want to know what we can do about it in practice.</p>
<p><strong>B:</strong> Then we need clear facts, not only opinions.</p>`;
}

/**
 * Discussion prompts (student-facing). Picks 5 questions from a varied pool
 * so the synthetic fallback does not look like a single hard-coded template.
 * @param {string} topic
 */
export function discussionQuestionsForTopic(topic) {
  const t = String(topic || 'this topic').trim() || 'this topic';
  const pool = [
    `Tell us about a personal experience connected to ${t}.`,
    `Why does ${t} matter in everyday life?`,
    `How has ${t} changed over the last 10–20 years?`,
    `What's the biggest myth or misunderstanding about ${t}?`,
    `Who benefits most from ${t}? Who loses out?`,
    `What single change would improve the situation around ${t}?`,
    `Compare ${t} in your country with another country you know.`,
    `Predict: where will ${t} be in 20 years?`,
    `What can ordinary people do about ${t}?`,
    `What surprised you most about ${t} when you first learned about it?`,
    `Would you rather know more or less about ${t}? Why?`,
    `Describe ${t} to someone who has never heard of it before — in 30 seconds.`,
    `Is ${t} more of an opportunity or a problem? Defend your view.`,
    `If you could ask one expert one question about ${t}, what would it be?`,
    `What's a common rule or assumption about ${t} that you'd break?`,
  ];
  // Rotate the pool deterministically per topic so different lessons get
  // different question sets — but the same topic always gets the same set.
  const seed = t.split('').reduce((a, c) => (a + c.charCodeAt(0)) % pool.length, 0);
  const picks = [];
  for (let i = 0; i < 5; i++) {
    picks.push({ question: pool[(seed + i * 4) % pool.length] });
  }
  return picks;
}
